import React, { useState, useEffect, useRef } from 'react';
import { Search, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Tabs, TabsList, TabsTrigger } from '../../ui/Tabs';
import { ChatWindow } from '../../cors/ChatWindow';
import { ReclamationsCard } from '../../elements/reclamation_card';
import { useSearchParams } from "react-router-dom"
import Cookies from 'js-cookie';
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from '../../apiConfig';
import Loading from '../../cors/Loading';

export default function ReclamationsPage() {
  const [openChats, setOpenChats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reclamations, setReclamations] = useState([]);
  const [filteredReclamations, setFilteredReclamations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalReclamations, setTotalReclamations] = useState(0);
  
  const closedOrderIds = useRef(new Set());
  
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get("orderId")
  const { token, role, roles } = useAuth();
  const user = Cookies.get('user') ? JSON.parse(Cookies.get('user')) : null;

  useEffect(() => {
    setOpenChats(prev =>
      prev.map(openChat => {
        const updated = reclamations.find(r => r.id === openChat.id);
        return updated ? updated : openChat;
      })
    );
  }, [reclamations]);

  useEffect(() => {
    let intervalId;

    const fetchReclamations = async () => {
      try {
        const url = new URL(`${BASE_URL}/api/reclamations`);
        url.searchParams.append('page', page);
        if (searchTerm) {
          url.searchParams.append('search', searchTerm);
        }
        if (statusFilter !== 'all') {
          url.searchParams.append('status', statusFilter);
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch reclamations");
        }

        const data = await response.json();
        setReclamations(data.data || data);
        setFilteredReclamations(data.data || data);
        setLastPage(data.last_page || 1);
        setTotalReclamations(data.total || data.length || 0);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching reclamations:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReclamations();

    intervalId = setInterval(fetchReclamations, 2000);

    return () => clearInterval(intervalId);
  }, [token, page, searchTerm, statusFilter]);

  // Fixed useEffect to handle orderId with manual close tracking
  useEffect(() => {
    if (orderId && !loading && !closedOrderIds.current.has(orderId)) {
      let reclamation = reclamations.find(
        r => r.order_id.toString() === orderId
      );

      // If no reclamation exists, create a placeholder
      if (!reclamation) {
        reclamation = {
          id: `temp-${orderId}`,       // temporary id
          order_id: orderId,
          order: { code: orderId },    // minimal order data
          messages: [],                // no messages yet
          isNew: true                  // mark as new
        };
      }

      // Open chat only if not already opened
      if (!openChats.find(chat => chat.order_id.toString() === orderId)) {
        setOpenChats(prev => [...prev, reclamation]);
      }
    }
  }, [orderId, reclamations, loading, openChats]); 

  useEffect(() => {
    if (reclamations.length > 0) {
      setOpenChats(prev =>
        prev.map(chat => {
          if (chat.isNew) {
            const realReclamation = reclamations.find(r => r.order_id.toString() === orderId);
            return realReclamation ? { ...realReclamation, isNew: false } : chat;
          }
          return chat;
        })
      );
    }
  }, [reclamations, orderId]);

  const openChat = (reclamation) => {
    // Remove from closed list when manually opened
    if (reclamation.order_id) {
      closedOrderIds.current.delete(reclamation.order_id.toString());
    }
    
    if (!openChats.find(chat => chat.id === reclamation.id)) {
      setOpenChats(prev => [...prev, reclamation]);
    }
  };

  const closeChat = (reclamationId) => {
    // Find the chat being closed to get its order_id
    const chatToClose = openChats.find(chat => chat.id === reclamationId);
    
    if (chatToClose && chatToClose.order_id) {
      // Add to closed list to prevent automatic reopening
      closedOrderIds.current.add(chatToClose.order_id.toString());
    }
    
    setOpenChats(prev => prev.filter(chat => chat.id !== reclamationId));
  };

  const refreshReclamations = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/reclamations?page=${page}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      
      if (!response.ok) {
        throw new Error('Failed to refresh reclamations');
      }
      
      const data = await response.json();
      setReclamations(data.data || data);
      setLastPage(data.last_page || 1);
      setTotalReclamations(data.total || data.length || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading reclamations</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshReclamations}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              Vorte Reclamations
            </h1>
            <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl">Gérez votre Reclamations</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by order code..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1); // Reset to first page when searching
                }}
                className="pl-10 outline-none"
              />
            </div>
            <Tabs value={statusFilter} onValueChange={(value) => {
              setStatusFilter(value);
              setPage(1); // Reset to first page when filtering
            }} className="w-full md:w-auto">
              <TabsList className="grid grid-cols-5 gap-2 max-lg:gap-1 w-full md:w-auto">
                <TabsTrigger value="all">Toute</TabsTrigger>
                <TabsTrigger value="notseen">Pas ouvert</TabsTrigger>
                <TabsTrigger value="seen">ouvert</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <span className="inline-flex items-center px-4 py-1.5 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px] font-medium">
            {totalReclamations} reclamations found
          </span>
        </div>
        {
          loading ? 
          <div className="mt-40 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loading />
            </div>
          </div>
      :
      <>
        
          {/* Reclamations Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredReclamations.map((reclamation) => (
              <ReclamationsCard
                key={reclamation.id}
                role={role}
                roles={roles?.reply_reclamation}
                reclamation={reclamation}
                onOpenChat={openChat}
                currentUser={user}
              />
            ))}
          </div>

          {filteredReclamations.length === 0 && !loading && (
            <div className="text-center pt-30">
              <div className="w-30 h-30 mx-auto mb-8 bg-[#e6f7f5] text-[var(--color-primary-500)] rounded-full flex items-center justify-center">
                <AlertCircle size={70} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No reclamations found</h3>
              <p className="text-gray-600 text-xs">Try adjusting your search or filter criteria.</p>
            </div>
          )}
          {/* Pagination Controls */}
          {filteredReclamations.length > 0 && (
            <div className="flex justify-center items-center mt-6 ">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`px-1 py-1 cursor-pointer rounded-md border ${page === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                    // Show first pages, current page with neighbors, or last pages
                    let pageNum;
                    if (lastPage <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= lastPage - 2) {
                      pageNum = lastPage - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 cursor-pointer text-sm rounded-md ${page === pageNum ? 'bg-[var(--color-primary-500)] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {lastPage > 5 && page < lastPage - 2 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  
                  {lastPage > 5 && page < lastPage - 2 && (
                    <button
                      onClick={() => setPage(lastPage)}
                      className={`px-3 py-1 cursor-pointer text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50`}
                    >
                      {lastPage}
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => setPage(prev => Math.min(prev + 1, lastPage))}
                  disabled={page === lastPage}
                  className={`px-1 py-1 cursor-pointer rounded-md border ${page === lastPage ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
            </div>
          )}

      </>
        }
      </div>

      {/* Chat Windows */}
      {openChats.map((reclamation, index) => (
        <ChatWindow
          key={reclamation.id}
          reclamation={reclamation}
          position={index}
          onClose={() => closeChat(reclamation.id)}
          onMessageSent={refreshReclamations} 
        />
      ))}
    </div>
  );
}